<template>
    <!-- Modo Minimal -->
    <div v-if="variant === 'minimal'" class="documentCard documentCard--minimal" :class="{ 'documentCard--loading': loading }" @click="openModal">
        <div class="documentCard_minimalHeader">
            <div class="documentCard_minimalIcon">
                <sw-icon :name="getDocumentIcon(document.type)" />
            </div>
            <div class="documentCard_minimalContent">
                <div class="documentCard_minimalName">{{ document.name }}</div>
                <div class="documentCard_minimalExpiry" v-if="document.expiryDate" :class="getExpiryClass(document.expiryDate)">
                    Vence em {{ getDaysUntilExpiry(document.expiryDate) }} dias
                </div>
            </div>
            <div class="documentCard_minimalArrow">
                <sw-icon name="arrow-right" />
            </div>
        </div>
    </div>

    <!-- Modo Default -->
    <div v-else class="documentCard" :class="{ 'documentCard--loading': loading }">
        <div class="documentCard_header">
            <div class="documentCard_icon" @click="openModal">
                <sw-icon :name="getDocumentIcon(document.type)" />
            </div>
            <h3 class="documentCard_name">{{ document.name }}</h3>
        </div>

        <div v-if="document.expiryDate" class="documentCard_statusArea">
            <div class="documentCard_statusContainer">
                <div class="documentCard_expiry" :class="getExpiryClass(document.expiryDate)">
                    <sw-icon :name="getExpiryIcon(document.expiryDate)" />
                    <span>{{ getExpiryMessage(document.expiryDate) }}</span>
                </div>
            </div>
        </div>

        <div class="documentCard_content">
            <div class="documentCard_meta">
                <span class="documentCard_type">{{ document.category }}</span>
                <span class="documentCard_type">{{ document.type }}</span>
            </div>
        </div>

        <div v-if="document.company" class="documentCard_company">
            <div class="documentCard_companyImage">
                <img
                    :src="document.company.image || 'https://www.fivebranches.edu/wp-content/uploads/2021/08/default-image.jpg'"
                    :alt="document.company.name"
                    @error="$event.target.src='https://www.fivebranches.edu/wp-content/uploads/2021/08/default-image.jpg'"
                />
            </div>
            <span class="documentCard_companyName">{{ document.company.name }}</span>
        </div>

        <div class="documentCard_footer">
            <div class="documentCard_actions">
                <!--name="Visualizar"-->
                <sw-button
                    icon="eye"
                    hover-color="blue"
                    size="sm"
                    height="sm"
                    circleIcon
                    @click="openModal"
                />
                <sw-button
                    name="Download"
                    icon="download"
                    hover-color="blue"
                    size="sm"
                    height="sm"
                    @click="downloadDocument"
                />
            </div>
        </div>
    </div>

    <!-- Modal de Visualização - Compartilhado entre ambos os modos -->
    <sw-modal
        :name="`viewDocumentModal_${document.id}`"
        :visible="showModal"
        @update="closeModal"
    >
        <template #header>
            <ModalHeader
                :icon="getDocumentIcon(selectedDocument?.type)"
                :title="selectedDocument?.name || 'Carregando...'"
                :subtitle="selectedDocument?.type || ''"
                color="blue"
            />
        </template>

        <template #content>
            <!-- Loading State -->
            <div class="modalContent_loading" v-if="loadingDocument">
                <app-spinner />
                <p>Carregando arquivo...</p>
            </div>

            <!-- Error State -->
            <div class="modalContent_error" v-else-if="documentError">
                <sw-icon name="cross" />
                <p>{{ documentError }}</p>
                <sw-button name="Tentar Novamente" color="blue" @click="retryLoadDocument" />
            </div>

            <!-- Document Content -->
            <div class="modalContent_document" v-else-if="selectedDocument">
                <TabsComponent
                    :tabs="documentTabs"
                    defaultTab="info"
                >
                    <!-- Aba Informações -->
                    <template #info>
                        <div class="modalContent_sections">
                            <div class="modal-section">
                                <h4>Informações Gerais</h4>
                                <div class="modalContent_grid">
                                    <div class="modalContent_field">
                                        <label>Nome:</label>
                                        <span>{{ selectedDocument.name }}</span>
                                    </div>
                                    <div class="modalContent_field" v-if="selectedDocument.category">
                                        <label>Categoria:</label>
                                        <span class="documentCard_type">{{ selectedDocument.category }}</span>
                                    </div>
                                    <div class="modalContent_field" v-if="selectedDocument.type">
                                        <label>Tipo:</label>
                                        <span class="documentCard_type">{{ selectedDocument.type }}</span>
                                    </div>
                                    <div class="modalContent_field">
                                        <label>Status:</label>
                                        <span :class="`modalContent_status modalContent_status--${selectedDocument.status}`">
                                            {{ getStatusText(selectedDocument.status) }}
                                        </span>
                                    </div>
                                </div>

                                <!-- Vencimento -->
                                <div class="modalContent_expiry" v-if="selectedDocument.expiryDate">
                                    <div class="modalContent_expiryHeader">
                                        <sw-icon name="calendar" />
                                        <label>Validade do Documento</label>
                                    </div>
                                    <div class="modalContent_expiryInfo" :class="getExpiryClass(selectedDocument.expiryDate)">
                                        <div class="modalContent_expiryDate">
                                            <sw-icon name="calendar" />
                                            {{ formatDate(selectedDocument.expiryDate) }}
                                        </div>
                                        <div class="modalContent_expiryStatus">
                                            <sw-icon :name="getExpiryIcon(selectedDocument.expiryDate)" />
                                            {{ getExpiryMessage(selectedDocument.expiryDate) }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="modal-section" v-if="selectedDocument.company">
                                <h4>Empresa</h4>
                                <div class="modalContent_company">
                                    <div class="modalContent_companyImage">
                                        <img
                                            :src="selectedDocument.company.image || 'https://www.fivebranches.edu/wp-content/uploads/2021/08/default-image.jpg'"
                                            :alt="selectedDocument.company.name"
                                            @error="$event.target.src='https://www.fivebranches.edu/wp-content/uploads/2021/08/default-image.jpg'"
                                        />
                                    </div>
                                    <div class="modalContent_companyInfo">
                                        <span class="modalContent_companyName">{{ selectedDocument.company.name }}</span>
                                    </div>
                                </div>
                            </div>


                            <div class="modal-section">
                                <h4>Detalhes do Arquivo</h4>
                                <div class="modalContent_grid">
                                    <div class="modalContent_field">
                                        <label>Número:</label>
                                        <span>{{ selectedDocument.documentNumber }}</span>
                                    </div>
                                    <div class="modalContent_field">
                                        <label>Adicionado em:</label>
                                        <span>{{ formatDate(selectedDocument.createdAt) }}</span>
                                    </div>
                                    <div class="modalContent_field">
                                        <label>Tamanho:</label>
                                        <span>{{ selectedDocument.fileSize }}</span>
                                    </div>
                                    <div class="modalContent_field">
                                        <label>Formato:</label>
                                        <span>{{ selectedDocument.fileFormat }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Modal Actions -->
                            <div class="modalActions">
                                <sw-button
                                    name="Download"
                                    color="blue"
                                    icon="download"
                                    @click="handleModalDownload"
                                    :disabled="downloadingDocument"
                                    v-if="selectedDocument && !loadingDocument && !documentError"
                                >
                                    <template v-if="downloadingDocument">Baixando...</template>
                                    <template v-else>Download</template>
                                </sw-button>

                                <sw-button
                                    name="Fechar"
                                    color="gray"
                                    @click="closeModal"
                                />
                            </div>
                        </div>
                    </template>

                    <!-- Aba Atividades/Logs -->
                    <template #logs>
                        <div class="modalContent_sections">
                            <div class="modal-section">
                                <h4>Histórico de Atividades</h4>
                                <div class="logs-list">
                                    <div
                                        v-for="log in documentLogs"
                                        :key="log.id"
                                        class="log-item"
                                    >
                                        <div class="log-icon">
                                            <sw-icon :name="getLogIcon(log.action)" />
                                        </div>
                                        <div class="log-content">
                                            <div class="log-description">{{ log.description }}</div>
                                            <div class="log-meta">
                                                <span class="log-user">{{ log.user }}</span>
                                                <span class="log-timestamp">{{ formatTimestamp(log.timestamp) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- Aba Atualizar -->
                    <template #update>
                        <div class="modalContent_sections">
                            <div class="modal-section">
                                <h4>Atualizar Arquivo</h4>
                            <div class="update-form">
                                <div class="form-field">
                                    <sw-input-file
                                        v-model="selectedFile"
                                        label="Selecionar novo arquivo"
                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                        @change="handleFileSelect"
                                    />
                                </div>


                                <div class="form-field">
                                    <sw-checkbox
                                        v-model="keepOldFile"
                                        label="Manter arquivo antigo (criar nova versão)"
                                    />
                                </div>

                                <div class="form-field">
                                    <sw-checkbox
                                        v-model="hasExpiryUpdate"
                                        label="Atualizar data de vencimento"
                                    />
                                </div>

                                <div v-if="hasExpiryUpdate" class="form-field">
                                    <sw-input
                                        v-model="newExpiryDate"
                                        label="Nova data de vencimento"
                                        :dateView="true"
                                        :minDate="moment().toDate()"
                                    />
                                </div>

                                <div class="form-actions">
                                    <sw-button
                                        name="Atualizar"
                                        color="blue"
                                        :disabled="!selectedFile || uploadingUpdate"
                                        @click="updateDocument"
                                    >
                                        <template v-if="uploadingUpdate">Atualizando...</template>
                                        <template v-else>Atualizar</template>
                                    </sw-button>
                                    <sw-button
                                        name="Cancelar"
                                        color="gray"
                                        @click="selectedFile = null; keepOldFile = false; hasExpiryUpdate = false; newExpiryDate = ''"
                                    />
                                </div>
                            </div>
                            </div>
                        </div>
                    </template>
                </TabsComponent>
            </div>
        </template>
    </sw-modal>
</template>

<script setup>
    import { ref } from 'vue';
    import moment from 'moment';
    import { fetchDocumentById, downloadDocument as apiDownloadDocument } from '@/services/mockApi';
    import {
        API_STATUS,
        FILE_STATUS_LABELS,
        DOCUMENT_GROUP_LABELS,
        DOCUMENT_GROUPS,
        FILE_CARD_VARIANTS,
        FILE_ICONS,
        EXPIRY_MESSAGES,
        NOTIFICATION_MESSAGES
    } from '@/constants';
    import ModalHeader from '@Component/modals/ModalHeader';
    import TabsComponent from '@Component/tabs/TabsComponent';

    const props = defineProps({
        document: {
            type: Object,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        variant: {
            type: String,
            default: FILE_CARD_VARIANTS.DEFAULT,
            validator: (value) => Object.values(FILE_CARD_VARIANTS).includes(value)
        }
    });

    const emit = defineEmits(['download']);

    // Modal state
    const showModal = ref(false);
    const selectedDocument = ref(null);
    const loadingDocument = ref(false);
    const documentError = ref(null);
    const downloadingDocument = ref(false);

    // Update state
    const uploadingUpdate = ref(false);
    const keepOldFile = ref(false);
    const selectedFile = ref(null);
    const hasExpiryUpdate = ref(false);
    const newExpiryDate = ref('');

    // Tabs state
    const documentTabs = ref([
        { id: 'info', label: 'Informações', icon: 'file-text' },
        { id: 'logs', label: 'Atividades', icon: 'clock' },
        { id: 'update', label: 'Atualizar', icon: 'upload' }
    ]);

    // Mock logs data
    const documentLogs = ref([
        {
            id: 1,
            action: 'download',
            description: 'Arquivo baixado',
            user: 'João Silva',
            timestamp: '2024-12-15T14:30:00Z'
        },
        {
            id: 2,
            action: 'upload',
            description: 'Arquivo adicionado ao sistema',
            user: 'João Silva',
            timestamp: '2024-12-01T10:30:00Z'
        }
    ]);

    const getDocumentIcon = (type) => {
        const iconMap = {
            // Identidade
            'CNH': 'credit-card',
            'RG': 'user',
            'CPF': 'user',
            'Título de Eleitor': 'users',
            'Passaporte': 'file-text',

            // Saúde
            'Carteira de Vacinação': 'file-text',

            // Comprovantes
            'Comprovante de Residência': 'home',

            // Veículos
            'CRLV': 'credit-card',
            'Seguro Veicular': 'file-text',

            // Empresariais
            'CNPJ': 'office',
            'Alvará': 'file-text',

            // Contratos
            'Contrato de Aluguel': 'home'
        };
        return iconMap[type] || 'file-text';
    };

    const getStatusText = (status) => {
        return FILE_STATUS_LABELS[status] || 'Desconhecido';
    };

    const getGroupName = (group) => {
        return DOCUMENT_GROUP_LABELS[group] || group;
    };

    const formatDate = (date) => {
        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(new Date(date));
    };

    const getExpiryClass = (expiryDate) => {
        if (!expiryDate) return '';

        const today = new Date();
        const expiry = new Date(expiryDate);
        const diffTime = expiry - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays <= 0) return 'documentCard_expired';
        if (diffDays <= 30) return 'documentCard_expiring';
        return 'documentCard_valid';
    };

    const getDaysUntilExpiry = (expiryDate) => {
        if (!expiryDate) return '';

        const today = new Date();
        const expiry = new Date(expiryDate);
        const diffTime = expiry - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays <= 0) {
            const overdueDays = Math.abs(diffDays);
            return overdueDays === 0 ? 0 : overdueDays;
        }
        return diffDays;
    };

    // Modal functions
    const openModal = async () => {
        console.log('FileCard: openModal chamado para documento:', props.document.id);
        showModal.value = true;
        console.log('FileCard: showModal definido como true');
        await loadSelectedDocument(props.document.id);
    };

    const closeModal = () => {
        showModal.value = false;
        selectedDocument.value = null;
        documentError.value = null;
        loadingDocument.value = false;
        downloadingDocument.value = false;
    };

    const loadSelectedDocument = async (documentId) => {
        console.log('FileCard: loadSelectedDocument iniciado para ID:', documentId);
        try {
            loadingDocument.value = true;
            documentError.value = null;
            selectedDocument.value = null;

            const response = await fetchDocumentById(documentId);

            if (response.status === API_STATUS.SUCCESS) {
                selectedDocument.value = response.data;
            } else {
                documentError.value = response.error.message;
            }
        } catch (err) {
            documentError.value = NOTIFICATION_MESSAGES.CONNECTION_ERROR;
            console.error('Erro ao carregar arquivo:', err);
        } finally {
            loadingDocument.value = false;
        }
    };

    const downloadDocument = async () => {
        try {
            console.log('Iniciando download do arquivo:', props.document.id);

            const response = await apiDownloadDocument(props.document.id);

            if (response.status === API_STATUS.SUCCESS) {
                // Simula download (em produção, seria um link real)
                const { downloadUrl, fileName } = response.data;
                console.log('Download URL gerado:', downloadUrl);

                // Criar elemento temporário para download
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = fileName;
                link.click();

                // Emitir evento para componente pai (se necessário)
                emit('download', props.document.id);
            } else {
                alert(NOTIFICATION_MESSAGES.UPLOAD_ERROR);
            }
        } catch (error) {
            console.error('Erro no download:', error);
            alert(NOTIFICATION_MESSAGES.UPLOAD_ERROR);
        }
    };

    const handleModalDownload = async () => {
        if (!selectedDocument.value?.id) return;

        try {
            downloadingDocument.value = true;

            const response = await apiDownloadDocument(selectedDocument.value.id);

            if (response.status === API_STATUS.SUCCESS) {
                const { downloadUrl, fileName } = response.data;

                // Criar elemento temporário para download
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = fileName;
                link.click();

                console.log('Download URL:', downloadUrl);

                // Emitir evento para componente pai (se necessário)
                emit('download', selectedDocument.value.id);
            } else {
                alert(NOTIFICATION_MESSAGES.UPLOAD_ERROR);
            }
        } catch (error) {
            console.error('Erro no download:', error);
            alert(NOTIFICATION_MESSAGES.UPLOAD_ERROR);
        } finally {
            downloadingDocument.value = false;
        }
    };

    const retryLoadDocument = () => {
        if (props.document?.id) {
            loadSelectedDocument(props.document.id);
        }
    };


    // Update functions
    const handleFileSelect = (event) => {
        selectedFile.value = event.target.files[0];
    };

    const updateDocument = async () => {
        if (!selectedFile.value) return;

        try {
            uploadingUpdate.value = true;

            // Simular upload
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Adicionar log da atualização
            documentLogs.value.unshift({
                id: documentLogs.value.length + 1,
                action: 'update',
                description: keepOldFile.value
                    ? 'Arquivo atualizado (versão anterior mantida)'
                    : 'Arquivo atualizado (versão anterior removida)',
                user: 'Usuário Atual',
                timestamp: new Date().toISOString()
            });

            // Reset form
            showUpdateForm.value = false;
            selectedFile.value = null;
            keepOldFile.value = false;

            alert('Arquivo atualizado com sucesso!');
        } catch (error) {
            alert('Erro ao atualizar arquivo');
        } finally {
            uploadingUpdate.value = false;
        }
    };

    const getLogIcon = (action) => {
        const iconMap = {
            'download': 'download',
            'upload': 'upload',
            'update': 'upload',
            'view': 'eye',
            'share': 'link'
        };
        return iconMap[action] || 'clock';
    };

    const formatTimestamp = (timestamp) => {
        return moment(timestamp).format('DD/MM/YYYY HH:mm');
    };

    // Helper functions for modal
    const getExpiryIcon = (expiryDate) => {
        if (!expiryDate) return 'calendar';

        const today = moment();
        const expiry = moment(expiryDate);
        const diffDays = expiry.diff(today, 'days');

        if (diffDays <= 0) return 'cross';
        if (diffDays <= 30) return 'notification';
        return 'checkmark';
    };

    const getExpiryMessage = (expiryDate) => {
        if (!expiryDate) return '';

        const today = moment();
        const expiry = moment(expiryDate);
        const diffDays = expiry.diff(today, 'days');

        if (diffDays <= 0) {
            const overdueDays = Math.abs(diffDays);
            return overdueDays === 0 ? EXPIRY_MESSAGES.TODAY : EXPIRY_MESSAGES.EXPIRED_DAYS(overdueDays);
        }
        if (diffDays <= 30) {
            return EXPIRY_MESSAGES.EXPIRES_DAYS(diffDays);
        }
        return EXPIRY_MESSAGES.VALID_DAYS(diffDays);
    };
</script>

<style lang="scss" scoped>
    // Import necessário para responsividade
    @import '@/assets/css/mediaScreen.scss';

    .documentCard {
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        padding: 1.25em;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;


        &:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
            border-color: #6366f1;

            .documentCard_icon {
                transform: scale(1.05);
            }
        }

        &--loading {
            opacity: 0.6;
            pointer-events: none;

            &::before {
                background:
                    linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
                animation: loading-shimmer 1.5s infinite;
            }
        }
    }

    @keyframes loading-shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
    }

    .documentCard_header {
        display: flex;
        align-items: center;
        gap: 1em;
        margin-bottom: 1em;
    }

    .documentCard_icon {
        width: 40px;
        height: 40px;
        border-radius: 12px;
        background: #f3f4f6;
        color: #6b7280;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25em;
        transition: all 0.3s ease;
        flex-shrink: 0;
        cursor: pointer;
        background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
        color: white;
        
        &:hover {
            background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
            
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
        }
    }


    .documentCard_statusArea {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5em;
        margin-bottom: 25px;
    }

    .documentCard_statusContainer {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5em;
    }

    .documentCard_badge {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 0.5em;
        font-size: 0.6875em;
        font-weight: 700;
        padding: 0.75em 1em;
        border-radius: 12px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        backdrop-filter: blur(10px);
        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        overflow: hidden;

        // Ícone indicador de status
        .documentCard_badgeIcon {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: currentColor;
            animation: pulse 2s ease-in-out infinite;
        }

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
            transition: left 0.6s ease;
        }

        &:hover::before {
            left: 100%;
        }

        &--active {
            background: linear-gradient(135deg,
                rgba(16, 185, 129, 0.15) 0%,
                rgba(5, 150, 105, 0.1) 100%);
            color: #059669;
            border: 1px solid rgba(16, 185, 129, 0.4);
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);

            .documentCard_badgeIcon {
                background: #10b981;
                box-shadow: 0 0 10px rgba(16, 185, 129, 0.5);
            }
        }

        &--expiring {
            background: linear-gradient(135deg,
                rgba(245, 158, 11, 0.15) 0%,
                rgba(217, 119, 6, 0.1) 100%);
            color: #d97706;
            border: 1px solid rgba(245, 158, 11, 0.4);
            box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);

            .documentCard_badgeIcon {
                background: #f59e0b;
                box-shadow: 0 0 10px rgba(245, 158, 11, 0.5);
            }
        }

        &--expired {
            background: linear-gradient(135deg,
                rgba(239, 68, 68, 0.15) 0%,
                rgba(220, 38, 38, 0.1) 100%);
            color: #dc2626;
            border: 1px solid rgba(239, 68, 68, 0.4);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);

            .documentCard_badgeIcon {
                background: #ef4444;
                box-shadow: 0 0 10px rgba(239, 68, 68, 0.5);
            }
        }
    }

    @keyframes pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.7; transform: scale(1.1); }
    }

    .documentCard_content {
        flex: 1;
        margin-bottom: 1em;
    }

    .documentCard_name {
        font-size: 1em;
        font-weight: 700;
        color: #1f2937;
        margin: 0;
        line-height: 1.4;
        flex: 1;
        transition: color 0.3s ease;

        &:hover {
            color: #6366f1;
        }
    }

    .documentCard_meta {
        display: flex;
        align-items: center;
        gap: 0.5em;
        margin-bottom: 0.75em;
        flex-wrap: wrap;
    }

    .documentCard_type {
        font-size: 0.6875em;
        font-weight: 600;
        color: #6366f1;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        background: rgba(99, 102, 241, 0.1);
        padding: 0.375em 0.625em;
        border-radius: 6px;
        border: 1px solid rgba(99, 102, 241, 0.2);
    }

    .documentCard_group {
        display: flex;
        align-items: center;
        gap: 0.375em;
        font-size: 0.6875em;
        font-weight: 500;
        color: #6b7280;
        background: rgba(107, 114, 128, 0.08);
        padding: 0.375em 0.625em;
        border-radius: 6px;
        border: 1px solid rgba(107, 114, 128, 0.15);

        .sw-icon {
            font-size: 0.75em;
        }
    }

    .documentCard_expiry {
        display: inline-flex;
        align-items: center;
        gap: 0.375em;
        padding: 0.5em 0.75em;
        border-radius: 1em;
        font-size: 0.75em;
        font-weight: 600;
        position: relative;
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);

        .sw-icon {
            font-size: 0.875em;
            flex-shrink: 0;
        }

        &.documentCard_expired {
            background: rgba(239, 68, 68, 0.1);
            color: #dc2626;
            box-shadow: 0 2px 4px rgba(239, 68, 68, 0.2);
        }

        &.documentCard_expiring {
            background: rgba(245, 158, 11, 0.1);
            color: #d97706;
            box-shadow: 0 2px 4px rgba(245, 158, 11, 0.2);
        }

        &.documentCard_valid {
            background: rgba(16, 185, 129, 0.1);
            color: #059669;
            box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
        }

        .sw-icon {
            font-size: 0.875em;
        }
    }

    .documentCard_company {
        display: flex;
        align-items: center;
        gap: 0.75em;
        margin-bottom: 1em;
        padding: 0.75em;
        background: rgba(248, 250, 252, 0.8);
        border-radius: 0.75em;
        border: 1px solid rgba(226, 232, 240, 0.6);
    }

    .documentCard_companyImage {
        width: 2em;
        height: 2em;
        border-radius: 0.5em;
        overflow: hidden;
        flex-shrink: 0;
        border: 1px solid rgba(226, 232, 240, 0.8);

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
        }
    }

    .documentCard_companyName {
        font-size: 0.8em;
        font-weight: 600;
        color: var(--textSecondary, #64748b);
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .documentCard_footer {
        margin-top: auto;
        padding-top: 1em;
        border-top: 1px solid #e5e7eb;
    }

    .documentCard_actions {
        display: flex;
        gap: 0.5em;
        justify-content: space-between;
        align-items: center;

        .sw-button {
            flex: 1;
            font-weight: 500;
            border-radius: 8px;
            font-size: 0.875em;
        }
    }

    /* =======================
       MINIMAL VARIANT STYLES
       ======================= */
    .documentCard--minimal {
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        transition: all 0.3s ease;
        padding: 1em;
        height: auto;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            border-color: #6366f1;

            .documentCard_minimalIcon .sw-icon {
                transform: scale(1.05);
            }

            .documentCard_minimalArrow {
                transform: translateX(2px);
                color: #6366f1;
            }
        }
    }

    .documentCard_minimalHeader {
        display: flex;
        align-items: center;
        gap: 0.75em;
    }

    .documentCard_minimalIcon {
        .sw-icon {
            width: 2.5em;
            height: 2.5em;
            background: #f3f4f6;
            color: #6b7280;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.125em;
            flex-shrink: 0;
            transition: all 0.3s ease;
        }

        .sw-icon:hover {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            box-shadow: 0 3px 8px rgba(16, 185, 129, 0.2);
            cursor: pointer;
        }
    }

    .documentCard_minimalContent {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.25em;
    }

    .documentCard_minimalName {
        font-size: 0.875em;
        font-weight: 600;
        color: var(--textPrimary, #1f2937);
        line-height: 1.3;
    }

    .documentCard_minimalExpiry {
        font-size: 0.75em;
        font-weight: 500;

        &.documentCard_expired {
            color: var(--colorRed, #dc2626);
        }

        &.documentCard_expiring {
            color: var(--colorOrange, #d97706);
        }

        &.documentCard_valid {
            color: var(--textSecondary, #6b7280);
        }
    }

    .documentCard_minimalArrow {
        color: var(--textSecondary, #6b7280);
        font-size: 0.875em;
        transition: transform 0.2s ease;

        .documentCard--minimal:hover & {
            transform: translateX(2px);
            color: var(--colorOrange, #f59e0b);
        }
    }

    /* =======================
       RESPONSIVIDADE
       ======================= */
    @include screen_medias(s, m) {
        .documentCard {
            padding: 1.125em;

            &:hover {
                transform: translateY(-3px);
            }
        }

        .documentCard_icon {
            width: 40px;
            height: 40px;
            font-size: 1.125em;
        }

        .documentCard_name {
            font-size: 1em;
        }

        .documentCard_meta {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5em;
        }

        .documentCard_actions {
            flex-direction: column;
            gap: 0.5em;
        }

        .documentCard--minimal {
            padding: 0.875em;

            &:hover {
                transform: translateY(-1px);
            }
        }

        .documentCard_minimalIcon .sw-icon {
            width: 2.25em;
            height: 2.25em;
            font-size: 1em;
        }
    }

    @include screen_s {
        .documentCard {
            padding: 1em;

            &:hover {
                transform: translateY(-2px);
            }
        }

        .documentCard_header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75em;
        }

        .documentCard_statusArea {
            align-items: flex-start;
            flex-direction: row;
            gap: 0.375em;
            flex-wrap: wrap;
        }

        .documentCard_icon {
            width: 40px;
            height: 40px;
            font-size: 1.125em;
        }

        .documentCard_name {
            font-size: 0.9375em;
        }

        .documentCard_type,
        .documentCard_group {
            font-size: 0.625em;
            padding: 0.325em 0.5em;
        }

        .documentCard--minimal {
            padding: 0.75em;
        }

        .documentCard_minimalIcon .sw-icon {
            width: 2em;
            height: 2em;
            font-size: 0.9375em;
        }

        .documentCard_minimalName {
            font-size: 0.8125em;
        }

        .documentCard_minimalExpiry {
            font-size: 0.6875em;
        }
    }

    /* =======================
       MODAL STYLES
       ======================= */
    .modalHeader {
        display: flex;
        align-items: center;
        gap: 1em;
        padding: 0;
    }

    .modalHeader_icon {
        width: 50px;
        height: 50px;
        border-radius: 0.75em;
        background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5em;
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }

    .modalHeader_title {
        display: flex;
        flex-direction: column;
        gap: 0.25em;
    }

    .modal-header-title {
        font-size: 1.25em;
        font-weight: 700;
        color: var(--textPrimary, #1f2937);
        margin: 0;
        line-height: 1.3;
    }

    .modal-header-subtitle {
        font-size: 0.875em;
        font-weight: 600;
        color: var(--colorBlue, #3b82f6);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        background: rgba(59, 130, 246, 0.1);
        padding: 0.25em 0.5em;
        border-radius: 0.375em;
        display: inline-block;
        width: fit-content;
    }

    .modalContent_loading,
    .modalContent_error {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3em 2em;
        gap: 1em;
        text-align: center;
        color: var(--textSecondary, #6b7280);
    }

    .modalContent_error .sw-icon {
        font-size: 3em;
        color: var(--colorRed, #dc2626);
    }

    .modalContent_document {
        padding: 0;
    }


    .modal-sections {
        display: flex;
        flex-direction: column;
        gap: 1.5em;
    }

    .modal-section {
        border: 1px solid var(--borderColorLight, #e5e7eb);
        border-radius: 0.75em;
        padding: 1.25em;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(248, 250, 252, 0.6));
        margin-bottom: 2em;

        h4 {
            font-size: 1em;
            font-weight: 700;
            color: var(--textPrimary, #1f2937);
            margin: 0 0 27px 0;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            border-bottom: 2px solid var(--colorBlue, #3b82f6);
            padding-bottom: 0.5em;
            display: inline-block;
        }

        h5 {
            font-size: 0.9em;
            font-weight: 600;
            color: var(--textPrimary, #1f2937);
            margin: 0 0 1em 0;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            border-bottom: 1px solid var(--borderColorLight, #e5e7eb);
            padding-bottom: 0.375em;
            display: inline-block;
        }
    }

    /* Estilo da seção já definido globalmente em modalSections.scss - usando classe .modal_section */

    .modalContent_grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 1em;
    }

    .modalContent_field {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75em;
        background: rgba(255, 255, 255, 0.8);
        border: 1px solid rgba(229, 231, 235, 0.8);
        border-radius: 0.5em;
        gap: 1em;
    }

    .modalContent_field label {
        font-size: 0.875em;
        font-weight: 600;
        color: var(--textSecondary, #6b7280);
        text-transform: uppercase;
        letter-spacing: 0.025em;
        min-width: fit-content;
    }

    .modalContent_field span {
        font-size: 0.875em;
        font-weight: 500;
        color: var(--textPrimary, #1f2937);
        text-align: right;
        word-break: break-word;
    }

    .modalContent_badge {
        background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
        color: white;
        padding: 0.375em 0.75em;
        border-radius: 0.5em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        font-size: 0.75em;
    }

    .modalContent_status {
        padding: 0.375em 0.75em;
        border-radius: 0.5em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        font-size: 0.75em;

        &--active {
            background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.1));
            color: var(--colorGreen, #059669);
            border: 1px solid rgba(16, 185, 129, 0.3);
        }

        &--expiring {
            background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.1));
            color: var(--colorOrange, #d97706);
            border: 1px solid rgba(245, 158, 11, 0.3);
        }

        &--expired {
            background: linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.1));
            color: var(--colorRed, #dc2626);
            border: 1px solid rgba(239, 68, 68, 0.3);
        }
    }

    .modalContent_expiry {
        display: flex;
        justify-content: center;
    }

    .modalContent_expiryDate {
        display: flex;
        align-items: center;
        gap: 1em;
        padding: 1em 1.5em;
        border-radius: 0.75em;
        font-weight: 600;
        width: fit-content;

        &.documentCard_expired {
            background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05));
            color: var(--colorRed, #dc2626);
            border: 2px solid rgba(239, 68, 68, 0.3);
        }

        &.documentCard_expiring {
            background: linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(217, 119, 6, 0.05));
            color: var(--colorOrange, #d97706);
            border: 2px solid rgba(245, 158, 11, 0.3);
        }

        &.documentCard_valid {
            background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.05));
            color: var(--colorGreen, #059669);
            border: 2px solid rgba(16, 185, 129, 0.3);
        }

        .sw-icon {
            font-size: 1.5em;
        }

        div {
            display: flex;
            flex-direction: column;
            gap: 0.25em;

            strong {
                font-size: 1.125em;
            }

            span {
                font-size: 0.875em;
                opacity: 0.9;
            }
        }
    }

    .modalContent_tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5em;
    }


    .modalContent_tag {
        background: linear-gradient(135deg, rgba(107, 114, 128, 0.1), rgba(75, 85, 99, 0.05));
        color: var(--textSecondary, #6b7280);
        padding: 0.375em 0.75em;
        border-radius: 0.5em;
        font-size: 0.8125em;
        font-weight: 500;
        border: 1px solid rgba(107, 114, 128, 0.2);
    }

    .modalActions {
        display: flex;
        justify-content: flex-end;
        gap: 0.75em;
        margin-top: 1.5em;
        padding-top: 1.5em;
        border-top: 1px solid var(--borderColorLight, #e5e7eb);
    }

    /* Responsive modal styles */
    @media (max-width: 768px) {
        .modalContent_grid {
            grid-template-columns: 1fr;
        }

        .modalContent_field {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5em;
        }

        .modalContent_field span {
            text-align: left;
        }

        .modalActions {
            flex-direction: column;
        }


        .modalContent_expiryDate {
            flex-direction: column;
            text-align: center;
        }
    }

    /* =======================
       ESTILOS PARA NOVAS FUNCIONALIDADES
       ======================= */

    // Company section in modal
    .modalContent_company {
        display: flex;
        align-items: center;
        gap: 1em;
        padding: 1em;
        background: rgba(248, 250, 252, 0.8);
        border-radius: 0.75em;
        border: 1px solid rgba(226, 232, 240, 0.6);
    }

    .modalContent_companyImage {
        width: 3em;
        height: 3em;
        border-radius: 0.75em;
        overflow: hidden;
        flex-shrink: 0;
        border: 1px solid rgba(226, 232, 240, 0.8);

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
        }
    }

    .modalContent_companyName {
        font-size: 1em;
        font-weight: 600;
        color: var(--textPrimary, #1f2937);
    }

    // Logs section
    .modalContent_logs {
        padding: 1em 0;

        h4 {
            margin-bottom: 1.5em;
            color: var(--textPrimary, #1f2937);
            font-size: 1.125em;
        }
    }

    .logs-list {
        display: flex;
        flex-direction: column;
        gap: 1em;
        max-height: 400px;
        overflow-y: auto;
    }

    .log-item {
        display: flex;
        align-items: flex-start;
        gap: 1em;
        padding: 1em;
        background: rgba(255, 255, 255, 0.8);
        border: 1px solid rgba(229, 231, 235, 0.8);
        border-radius: 0.75em;
        transition: all 0.2s ease;

        &:hover {
            background: rgba(248, 250, 252, 0.9);
            border-color: rgba(99, 102, 241, 0.3);
        }
    }

    .log-icon {
        width: 2.5em;
        height: 2.5em;
        background: linear-gradient(135deg, #6366f1, #8b5cf6);
        color: white;
        border-radius: 0.5em;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        font-size: 0.875em;
    }

    .log-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.5em;
    }

    .log-description {
        font-size: 0.875em;
        font-weight: 500;
        color: var(--textPrimary, #1f2937);
        line-height: 1.4;
    }

    .log-meta {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 0.75em;
        color: var(--textSecondary, #6b7280);
    }

    .log-user {
        font-weight: 500;
    }

    .log-timestamp {
        font-weight: 400;
    }

    // Update section
    .modalContent_update {
        padding: 1em 0;

        h4 {
            margin-bottom: 1.5em;
            color: var(--textPrimary, #1f2937);
            font-size: 1.125em;
        }
    }

    .update-form {
        display: flex;
        flex-direction: column;
        gap: 1.5em;

        p {
            color: var(--textSecondary, #6b7280);
            font-size: 0.875em;
            line-height: 1.5;
            margin: 0;
        }
    }

    .form-field {
        display: flex;
        flex-direction: column;
        gap: 0.75em;

        label {
            font-size: 0.875em;
            font-weight: 600;
            color: var(--textSecondary, #6b7280);
        }
    }

    .file-input {
        padding: 0.75em;
        border: 2px dashed #d1d5db;
        border-radius: 0.5em;
        background: #f9fafb;
        transition: all 0.2s ease;
        cursor: pointer;

        &:hover {
            border-color: #6366f1;
            background: #f3f4f6;
        }

        &:focus {
            outline: none;
            border-color: #6366f1;
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
    }

    .selected-file {
        font-size: 0.875em;
        color: var(--colorBlue, #3b82f6);
        font-weight: 500;
        padding: 0.5em;
        background: rgba(59, 130, 246, 0.1);
        border-radius: 0.375em;
        border: 1px solid rgba(59, 130, 246, 0.2);
    }

    .checkbox-label {
        display: flex;
        align-items: center;
        gap: 0.75em;
        font-size: 0.875em;
        color: var(--textPrimary, #1f2937);
        cursor: pointer;

        input[type="checkbox"] {
            width: 1.125em;
            height: 1.125em;
            accent-color: #6366f1;
        }
    }

    .form-actions {
        display: flex;
        gap: 0.75em;
        justify-content: flex-start;
        margin-top: 1em;
    }

    .modalContent_expiry {
        display: flex;
        flex-direction: column;
        gap: 0.875em;
        margin-top: 1.25em;
        padding: 1.25em;
        background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
        border: 1px solid #e2e8f0;
        border-radius: 1em;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 2px;
            background: linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899);
            opacity: 0.6;
        }

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border-color: #cbd5e1;
        }
    }

    .modalContent_expiryHeader {
        display: flex;
        align-items: center;
        gap: 0.625em;

        .sw-icon {
            color: #6366f1;
            font-size: 1.125em;
            padding: 0.375em;
            background: rgba(99, 102, 241, 0.1);
            border-radius: 0.5em;
            transition: all 0.3s ease;
        }

        label {
            font-weight: 600;
            color: #1e293b;
            font-size: 0.9em;
            letter-spacing: 0.025em;
            text-transform: uppercase;
            opacity: 0.9;
        }
    }

    .modalContent_expiryInfo {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1.25em;
        padding: 1em 1.25em;
        border-radius: 0.75em;
        border: 1px solid;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 4px;
            height: 100%;
            transition: all 0.3s ease;
        }

        &.documentCard_expired {
            background: linear-gradient(135deg, rgba(239, 68, 68, 0.06), rgba(220, 38, 38, 0.02));
            border-color: rgba(239, 68, 68, 0.2);
            box-shadow: 0 2px 8px rgba(239, 68, 68, 0.08);

            &::before {
                background: linear-gradient(180deg, #ef4444, #dc2626);
            }

            .modalContent_expiryDate {
                color: #b91c1c;
                font-weight: 700;
            }

            .modalContent_expiryStatus {
                color: #dc2626;

                .sw-icon {
                    color: #dc2626;
                    background: rgba(220, 38, 38, 0.1);
                    padding: 0.25em;
                    border-radius: 0.375em;
                }
            }
        }

        &.documentCard_expiring {
            background: linear-gradient(135deg, rgba(245, 158, 11, 0.06), rgba(217, 119, 6, 0.02));
            border-color: rgba(245, 158, 11, 0.2);
            box-shadow: 0 2px 8px rgba(245, 158, 11, 0.08);

            &::before {
                background: linear-gradient(180deg, #f59e0b, #d97706);
            }

            .modalContent_expiryDate {
                color: #92400e;
                font-weight: 700;
            }

            .modalContent_expiryStatus {
                color: #d97706;

                .sw-icon {
                    color: #d97706;
                    background: rgba(217, 119, 6, 0.1);
                    padding: 0.25em;
                    border-radius: 0.375em;
                }
            }
        }

        &.documentCard_valid {
            background: linear-gradient(135deg, rgba(16, 185, 129, 0.06), rgba(5, 150, 105, 0.02));
            border-color: rgba(16, 185, 129, 0.2);
            box-shadow: 0 2px 8px rgba(16, 185, 129, 0.08);

            &::before {
                background: linear-gradient(180deg, #10b981, #059669);
            }

            .modalContent_expiryDate {
                color: #047857;
                font-weight: 700;
            }

            .modalContent_expiryStatus {
                color: #059669;

                .sw-icon {
                    color: #059669;
                    background: rgba(5, 150, 105, 0.1);
                    padding: 0.25em;
                    border-radius: 0.375em;
                }
            }
        }

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }
    }

    .modalContent_expiryDate {
        font-size: 0.875em;
        font-weight: 600;
        letter-spacing: 0.02em;
        line-height: 1.3;
        display: flex;
        align-items: center;
        gap: 0.375em;
        padding: 0.375em 0.75em;
        background: rgba(255, 255, 255, 0.6);
        border-radius: 0.5em;
        border: 1px solid rgba(226, 232, 240, 0.6);
        backdrop-filter: blur(8px);
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
            transition: left 0.6s ease;
        }

        &:hover::before {
            left: 100%;
        }

        .sw-icon {
            font-size: 0.9em;
            opacity: 0.8;
        }
    }

    .modalContent_expiryStatus {
        display: flex;
        align-items: center;
        gap: 0.5em;
        font-size: 0.875em;
        font-weight: 600;
        opacity: 0.95;
        white-space: nowrap;

        .sw-icon {
            font-size: 0.875em;
            transition: all 0.3s ease;
        }
    }

    /* Mobile responsiveness for new features */
    @include screen_medias(s, m) {
        .modalContent_company {
            flex-direction: column;
            text-align: center;
            gap: 0.75em;
        }

        .modalContent_expiry {
            padding: 0.75em;
            margin-top: 0.75em;
        }

        .modalContent_expiryInfo {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5em;
            padding: 0.5em 0.75em;
        }

        .modalContent_expiryDate {
            font-size: 1em;
        }

        .modalContent_expiryStatus {
            font-size: 0.8em;
        }

        .log-item {
            gap: 0.75em;
        }

        .log-meta {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25em;
        }

        .form-actions {
            flex-direction: column;
        }
    }
</style>